

import { BrowserRouter, Routes, Route, Navigate, Outlet } from "react-router-dom";
import { useEffect } from "react";
import { ToastContainer } from "react-toastify";
import { SyncIndicator } from './components/SyncIndicator'; // Yolu projenize göre ayarlayabilirsiniz

// Layout & Store & Routes
import { Layout } from "./components/Layout";
import { ProtectedRoute } from "./routes/ProtectedRoute";
import { useAuthStore } from "./store/authStore";
import { Splash } from "./components/Splash";
import { useUIStore } from "./store/uiStore";

// Sayfa Bileşenleri
import { Login } from "./pages/auth/Login";
import { NotFound } from "./pages/NotFound";
import { Unauthorized } from "./pages/Unauthorized";
import { AccountSettings } from "./pages/AccountSettings";

// Admin Sayfaları
import { AdminDashboard } from "./pages/admin/AdminDashboard";
import { FirmList } from "./pages/admin/FirmList";
import { UserList } from "./pages/admin/user/UserList";
import { IntegrationAppList } from "./pages/admin/IntegrationAppList";
import { RoleList } from "./pages/admin/user/RoleList";
import { UnitList } from "./pages/admin/unit/UnitList";

// Firm Sayfaları
import { FirmDashboard } from "./pages/firm/FirmDashboard";
import { ProductList } from "./pages/firm/product/ProductList";
import { CustomerList } from "./pages/firm/customer/CustomerList";
import { WarehouseList } from "./pages/firm/warehouse/WarehouseList";
import { StockRollsList } from './pages/firm/inventory/StockRollsList';
import { CashList } from "./pages/firm/cash/CashList";
import { BankList } from "./pages/firm/bank/BankList";
import { OrderList } from "./pages/firm/orders/OrderList";
import { InvoiceList } from "./pages/firm/invoices/InvoiceList";
import { CollectionPaymentList } from "./pages/firm/finance/CollectionPaymentList";
import { WarehouseTransferList } from "./pages/firm/inventory/WarehouseTransferList";
import { FirmUserList } from "./pages/firm/user/FirmUserList";
import { WarehouseMonitor } from "./pages/firm/warehouse/WarehouseMonitor";
import { LiveMonitor } from "./pages/firm/warehouse/LiveMonitor";
import { WarehouseItemTrackingList } from "./pages/firm/warehouse/WarehouseItemTrackingList";
import { BrandList } from "./pages/firm/brand/BrandList";
import { CategoryList } from "./pages/firm/category/CategoryList";
import { EmployeeList } from "./pages/firm/employee/EmployeeList";
import { FirmSettingsPage } from "./pages/settings/FirmSettings";
import { FirmRoleList } from "./pages/firm/role/FirmRoleList";
import { CargoIntegrationSettings } from "./pages/settings/CargoIntegrationSettings";
import { EInvoiceConfig } from "./pages/settings/EInvoiceConfig";
import { SanalPosConfig } from "./pages/settings/SanalPosConfig";
// Raporlar & Ziyaret
import { StockReports } from "./pages/firm/reports/StockReports";
import { FinanceReports } from "./pages/firm/reports/FinanceReports";
import { SalesAnalysis } from "./pages/firm/reports/SalesAnalysis";
import { VisitMonitorList } from "./pages/firm/visit/VisitMonitorList";
import { VisitPlanner } from "./pages/firm/visit/VisitPlanner";
import { VisitReports } from "./pages/firm/visit/VisitReports";

// Customer Sayfaları
import { CustomerDashboard } from "./pages/customer/CustomerDashboard";
// 🌟 DEĞİŞİKLİK: PrimeReactProvider import edildi
import { addLocale, locale, PrimeReactProvider } from "primereact/api";
import { LabelTemplateList } from "./pages/firm/label/label_template_list";
import { LabelDesigner } from "./pages/firm/label/label_designer";
import { PrintSettings } from "./pages/firm/label/PrintSettings";
import { EmployeeHistoryReport } from "./pages/firm/warehouse/EmployeeHistoryReport";
import { PrinterList } from "./pages/firm/printer/PrinterList";
import { ScaleDeviceList } from "./pages/firm/scale-device/ScaleDeviceList";
import { FirmAppList } from "./pages/admin/FirmAppList";
import { DespatchList } from "./pages/firm/despatch/DespatchList";
import { StaffPerformanceReport } from "./pages/firm/warehouse/StaffPerformanceReport";
import { SettingDefinitionManagement } from "./pages/admin/SettingDefinitionManagement";
import { WarehouseShortageMonitor } from "./pages/firm/warehouse/WarehouseShortageMonitor";

const MainLayoutWrapper = () => {
  const user = useAuthStore((s) => s.user);
  return (
    <Layout role={user?.role?.name || ""}>
      <Outlet />
    </Layout>
  );
};

addLocale('tr', {
  firstDayOfWeek: 1,
  dayNames: ['Pazar', 'Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma', 'Cumartesi'],
  dayNamesShort: ['Paz', 'Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt'],
  dayNamesMin: ['Pz', 'Pt', 'Sa', 'Ça', 'Pe', 'Cu', 'Ct'],
  monthNames: ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran', 'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'],
  monthNamesShort: ['Oca', 'Şub', 'Mar', 'Nis', 'May', 'Haz', 'Tem', 'Ağu', 'Eyl', 'Eki', 'Kas', 'Ara'],
  today: 'Bugün',
  clear: 'Temizle'
});

function App() {
  const { user, checkAuth, isInitializing } = useAuthStore();
  const splash = useUIStore((s) => s.splash);

  locale('tr');

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const getDashboardPath = (roleName: string) => {
    if (["super_admin", "admin"].includes(roleName)) return "/admin/dashboard";
    if (roleName === "customer") return "/customer/dashboard";
    return "/firm/dashboard"; // Bütün diğer (özel) firma rolleri için
  };

  if (isInitializing) {
    return <Splash show={true} fullScreen={true} />;
  }

  return (
    // 🌟 DEĞİŞİKLİK: Boş fragment (<>) yerine PrimeReactProvider ile sarmaladık
    <PrimeReactProvider>
      <Splash show={splash} fullScreen={false} />
      <SyncIndicator />
      <BrowserRouter>
        <ToastContainer
          position="top-right"
          autoClose={2000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="colored"
        />
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/unauthorized" element={<Unauthorized />} />

          <Route
            path="/"
            element={
              user ? (
                <Navigate to={getDashboardPath(user.role.name)} replace />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />

          <Route element={<MainLayoutWrapper />}>
            <Route path="/account-setting" element={<AccountSettings />} />

            {/* 1. ADMIN & SUPER_ADMIN */}
            <Route element={<ProtectedRoute roles={["admin", "super_admin"]} />}>
              <Route path="/admin/dashboard" element={<AdminDashboard />} />
              <Route path="/admin/firms" element={<FirmList />} />
              <Route path="/admin/apps" element={<FirmAppList />} />
              <Route path="/admin/setting-definition" element={<SettingDefinitionManagement />} />
              <Route path="/admin/users" element={<UserList />} />
              <Route path="/admin/roles" element={<RoleList />} />
              <Route path="/admin/unit" element={<UnitList />} />
              <Route path="/admin/integration/applications" element={<IntegrationAppList />} />
            </Route>

            {/* 2. FIRM_ADMIN & FIRM_SALES & CUSTOM ROLES */}
            <Route element={<ProtectedRoute excludeRoles={["customer"]} />}>
              <Route path="/firm/dashboard" element={<FirmDashboard />} />

              {/* Stok & Depo Grubu */}
              <Route path="/firm/stocks" element={<ProtectedRoute module="stockModule"><ProductList /></ProtectedRoute>} />
              <Route path="/firm/stock-rolls" element={<ProtectedRoute module="stockModule"><StockRollsList /></ProtectedRoute>} />
              <Route path="/firm/category" element={<ProtectedRoute module="stockModule"><CategoryList /></ProtectedRoute>} />
              <Route path="/firm/brand" element={<ProtectedRoute module="stockModule"><BrandList /></ProtectedRoute>} />
              <Route path="/firm/stock-movements" element={<ProtectedRoute module="stockModule"><WarehouseTransferList /></ProtectedRoute>} />
              <Route path="/firm/warehouses" element={<ProtectedRoute module="warehouseModule"><WarehouseList /></ProtectedRoute>} />
              <Route path="/firm/warehouses/monitor" element={<ProtectedRoute module="warehouseModule"><WarehouseMonitor /></ProtectedRoute>} />
              <Route path="/firm/warehouses/item-tracking" element={<ProtectedRoute module="warehouseModule"><WarehouseItemTrackingList /></ProtectedRoute>} />
              <Route path="/firm/warehouses/live-monitor" element={<ProtectedRoute module="warehouseModule"><LiveMonitor /></ProtectedRoute>} />
              <Route path="/firm/warehouses/shortage-monitor" element={<ProtectedRoute module="warehouseModule"><WarehouseShortageMonitor /></ProtectedRoute>} />
              <Route path="/firm/warehouses/employee-history" element={<ProtectedRoute module="warehouseModule"><EmployeeHistoryReport /></ProtectedRoute>} />
              <Route path="/firm/warehouses/employee-performance" element={<ProtectedRoute module="warehouseModule"><StaffPerformanceReport /></ProtectedRoute>} />

              {/* Müşteri & Satış Grubu */}
              <Route path="/firm/customers" element={<ProtectedRoute module="accountModule"><CustomerList /></ProtectedRoute>} />
              <Route path="/firm/orders" element={<ProtectedRoute module="orderModule"><OrderList /></ProtectedRoute>} />
              <Route path="/firm/invoices" element={<ProtectedRoute module="invoiceModule"><InvoiceList /></ProtectedRoute>} />
              <Route path="/firm/despatch" element={<ProtectedRoute module="invoiceModule"><DespatchList /></ProtectedRoute>} />
              {/* Finans & Nakit Grubu */}
              <Route path="/firm/collections-payments" element={<ProtectedRoute module="paymentModule"><CollectionPaymentList /></ProtectedRoute>} />
              <Route path="/firm/cashes" element={<ProtectedRoute module="financeModule"><CashList /></ProtectedRoute>} />
              <Route path="/firm/banks" element={<ProtectedRoute module="financeModule"><BankList /></ProtectedRoute>} />

              {/* Personel & Sistem Grubu */}
              <Route path="/firm/users" element={<ProtectedRoute module="userModule"><FirmUserList /></ProtectedRoute>} />
              <Route path="/firm/employee" element={<ProtectedRoute module="userModule"><EmployeeList /></ProtectedRoute>} />
              <Route path="/firm/roles" element={<ProtectedRoute roles={["super_admin", "firm_admin"]} module="userModule"><FirmRoleList /></ProtectedRoute>} />
              <Route path="/firm/settings" element={<ProtectedRoute module="syncModule"><FirmSettingsPage /></ProtectedRoute>} />
              <Route path="/firm/settings/cargo" element={<ProtectedRoute module="syncModule"><CargoIntegrationSettings /></ProtectedRoute>} />
              <Route path="/firm/settings/einvoice" element={<ProtectedRoute module="syncModule"><EInvoiceConfig /></ProtectedRoute>} />
              <Route path="/firm/settings/sanalpos" element={<ProtectedRoute module="syncModule"><SanalPosConfig /></ProtectedRoute>} />

              {/* Cihazlar */}
              <Route path="/firm/labels" element={<ProtectedRoute module="syncModule"><LabelTemplateList /></ProtectedRoute>} />
              <Route path="/firm/labels/designer/:templateId" element={<ProtectedRoute module="syncModule"><LabelDesigner /></ProtectedRoute>} />
              <Route path="/firm/printers" element={<ProtectedRoute module="stockModule"><PrinterList /></ProtectedRoute>} />
              <Route path="/firm/scale-devices" element={<ProtectedRoute module="warehouseModule"><ScaleDeviceList /></ProtectedRoute>} />
              <Route path="/firm/print-settings" element={<ProtectedRoute module="syncModule"><PrintSettings /></ProtectedRoute>} />

              {/* Saha Operasyonu */}
              <Route path="/firm/visit/monitor" element={<ProtectedRoute module="visitModule"><VisitMonitorList /></ProtectedRoute>} />
              <Route path="/firm/visit/planner" element={<ProtectedRoute module="visitModule"><VisitPlanner /></ProtectedRoute>} />

              {/* Raporlar */}
              <Route path="/firm/reports/stock" element={<ProtectedRoute module="stockModule"><StockReports /></ProtectedRoute>} />
              <Route path="/firm/reports/finance" element={<ProtectedRoute module="financeModule"><FinanceReports /></ProtectedRoute>} />
              <Route path="/firm/reports/sales" element={<ProtectedRoute module="orderModule"><SalesAnalysis /></ProtectedRoute>} />
              <Route path="/firm/reports/visits" element={<ProtectedRoute module="visitModule"><VisitReports /></ProtectedRoute>} />
            </Route>

            {/* 3. CUSTOMER */}
            <Route element={<ProtectedRoute roles={["customer"]} />}>
              <Route path="/customer/dashboard" element={<CustomerDashboard />} />
            </Route>
          </Route>

          <Route path="*" element={<NotFound />} />
        </Routes>
      </BrowserRouter>
    </PrimeReactProvider>
  );
}

export default App;